import { createAction, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  AppNoticeDto,
  NoticeType,
  AppNoticePayload,
} from "../../types/appNotice.definition";

interface AppNoticeState {
  rules: AppNoticeDto[];
  loadedFeatureType: NoticeType | "ALL" | null;
  loading: boolean;
  saving: boolean;
  deleting: boolean;
  error: string | null;
}

const initialState: AppNoticeState = {
  rules: [],
  loadedFeatureType: null,
  loading: false,
  saving: false,
  deleting: false,
  error: null,
};

// ── Actions ───────────────────────────────────────────────────────────────

export const fetchAppNoticesRequest = createAction<{
  featureType?: NoticeType;
}>("appNotices/fetchAppNoticesRequest");

export const fetchAppNoticesSuccess = createAction<{
  data: AppNoticeDto[];
  featureType: NoticeType | "ALL";
}>("appNotices/fetchAppNoticesSuccess");

export const fetchAppNoticesFailure = createAction<string>(
  "appNotices/fetchAppNoticesFailure"
);

export const createAppNoticeRequest = createAction<{
  data: AppNoticePayload;
  onSuccess?: (rule: AppNoticeDto) => void;
  onError?: (code: string) => void;
}>("appNotices/createAppNoticeRequest");

export const createAppNoticeSuccess = createAction<AppNoticeDto>(
  "appNotices/createAppNoticeSuccess"
);
export const createAppNoticeFailure = createAction<string>(
  "appNotices/createAppNoticeFailure"
);

export const updateAppNoticeRequest = createAction<{
  id: number;
  data: AppNoticePayload;
  onSuccess?: (rule: AppNoticeDto) => void;
  onError?: (code: string) => void;
}>("appNotices/updateAppNoticeRequest");

export const updateAppNoticeSuccess = createAction<AppNoticeDto>(
  "appNotices/updateAppNoticeSuccess"
);
export const updateAppNoticeFailure = createAction<string>(
  "appNotices/updateAppNoticeFailure"
);

export const deleteAppNoticeRequest = createAction<{
  id: number;
  onSuccess?: () => void;
  onError?: (code: string) => void;
}>("appNotices/deleteAppNoticeRequest");

export const deleteAppNoticeSuccess = createAction<number>(
  "appNotices/deleteAppNoticeSuccess"
);
export const deleteAppNoticeFailure = createAction<string>(
  "appNotices/deleteAppNoticeFailure"
);

// ── Slice ─────────────────────────────────────────────────────────────────

const appNoticeSlice = createSlice({
  name: "appNotices",
  initialState,
  reducers: {
    clearError: state => {
      state.error = null;
    },
  },
  extraReducers: builder => {
    // Fetch
    builder
      .addCase(fetchAppNoticesRequest, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchAppNoticesSuccess,
        (
          state,
          action: PayloadAction<{
            data: AppNoticeDto[];
            featureType: NoticeType | "ALL";
          }>
        ) => {
          state.loading = false;
          state.rules = action.payload.data;
          state.loadedFeatureType = action.payload.featureType;
          state.error = null;
        }
      )
      .addCase(
        fetchAppNoticesFailure,
        (state, action: PayloadAction<string>) => {
          state.loading = false;
          state.error = action.payload;
        }
      );

    // Create
    builder
      .addCase(createAppNoticeRequest, state => {
        state.saving = true;
        state.error = null;
      })
      .addCase(
        createAppNoticeSuccess,
        (state, action: PayloadAction<AppNoticeDto>) => {
          state.saving = false;
          state.error = null;
          // Insert the new rule at the top
          state.rules = [action.payload, ...state.rules];
        }
      )
      .addCase(
        createAppNoticeFailure,
        (state, action: PayloadAction<string>) => {
          state.saving = false;
          state.error = action.payload;
        }
      );

    // Update
    builder
      .addCase(updateAppNoticeRequest, state => {
        state.saving = true;
        state.error = null;
      })
      .addCase(
        updateAppNoticeSuccess,
        (state, action: PayloadAction<AppNoticeDto>) => {
          state.saving = false;
          state.error = null;
          state.rules = state.rules.map(r =>
            r.id === action.payload.id ? action.payload : r
          );
        }
      )
      .addCase(
        updateAppNoticeFailure,
        (state, action: PayloadAction<string>) => {
          state.saving = false;
          state.error = action.payload;
        }
      );

    // Delete
    builder
      .addCase(deleteAppNoticeRequest, state => {
        state.deleting = true;
        state.error = null;
      })
      .addCase(
        deleteAppNoticeSuccess,
        (state, action: PayloadAction<number>) => {
          state.deleting = false;
          state.rules = state.rules.filter(r => r.id !== action.payload);
          state.error = null;
        }
      )
      .addCase(
        deleteAppNoticeFailure,
        (state, action: PayloadAction<string>) => {
          state.deleting = false;
          state.error = action.payload;
        }
      );
  },
});

export const { clearError } = appNoticeSlice.actions;

// ── Selectors ─────────────────────────────────────────────────────────────

export const selectAppNoticeRules = (state: { appNotices: AppNoticeState }) =>
  state.appNotices.rules;
export const selectAppNoticeLoading = (state: { appNotices: AppNoticeState }) =>
  state.appNotices.loading;
export const selectAppNoticeSaving = (state: { appNotices: AppNoticeState }) =>
  state.appNotices.saving;
export const selectAppNoticeDeleting = (state: {
  appNotices: AppNoticeState;
}) => state.appNotices.deleting;
export const selectAppNoticeError = (state: { appNotices: AppNoticeState }) =>
  state.appNotices.error;
export const selectAppNoticeLoadedFeatureType = (state: {
  appNotices: AppNoticeState;
}) => state.appNotices.loadedFeatureType;

export default appNoticeSlice.reducer;
